<template>
  <div class="club-member" :class="{'expanded': expanded}">
    <router-link :to="`/oscar-pool/${club_id}/member/${member._id}`" class="member-link">
      <ProfilePicture :user="member" :size="20"></ProfilePicture> <ProfileName :user="member"></ProfileName>
    </router-link>
    <div v-if="expanded" class="member-name-button-group">
      <button v-if="show_make_admin_button" class="btn btn-sm btn-member-menu btn-admin-change" type="button" @click.stop="makeAdmin()">
        make admin <i class="fa fa-level-up"></i>
      </button>
      <button v-if="show_transfer_ownership_button" class="btn btn-member-menu btn-sm btn-ownership-change" type="button" @click.stop="transferClubOwnership()">
        make new owner <i class="fa fa-star"></i>
      </button>
      <button v-if="show_revoke_admin_button" class="btn btn-sm btn-member-menu btn-admin-change" type="button" @click.stop="revokeAdmin()">
        revoke admin <i class="fa fa-level-down"></i>
      </button>
      <button v-if="show_remove_member_button" class="btn btn-sm btn-member-menu btn-leave-club" type="button" @click.stop="removeMember()">
        remove <i class="fa fa-sign-out"></i>
      </button>
      <button v-if="show_leave_club_button" class="btn btn-sm btn-member-menu btn-leave-club" type="button" @click.stop="leaveClub()">
        leave pool <i class="fa fa-sign-out"></i>
      </button>
    </div>
    <div v-if="has_buttons_to_show" class="expand-button">
      <i class="fa" :class="expanded? 'fa-caret-up' : 'fa-caret-down'" aria-hidden="true"></i>
    </div>
  </div>
</template>


<script>
import ProfilePicture from "@/components/profile/ProfilePicture.vue";
import ProfileName from "@/components/profile/ProfileName.vue";
import axios from "axios";
import {userStore} from "@/userStore";

export default {
  name: "ClubMember",
  components: {ProfileName, ProfilePicture},
  props: ['club_id', 'member', 'show_buttons', 'user_role'],
  emits: ['club-updated'],
  computed: {

    expanded() {
      return this.show_buttons && this.has_buttons_to_show;
    },

    has_buttons_to_show() {
      return (
          this.show_make_admin_button ||
          this.show_revoke_admin_button ||
          this.show_remove_member_button ||
          this.show_leave_club_button ||
          this.show_transfer_ownership_button
      );
    },

    show_make_admin_button() {
      // don't show if user isn't an owner or admin
      if ( (this.user_role !== 'owner') && (this.user_role !== 'admin') ) {
        return false;
      }
      // don't show if the member is an owner or already an admin
      if ( (this.member?.role === 'owner') || (this.member?.role === 'admin') ) {
        return false;
      }
      return true;
    },

    show_revoke_admin_button() {
      // don't show if user isn't an owner
      if ( this.user_role !== 'owner' ) {
        return false;
      }
      // don't show if the member is an owner or already not an admin
      if ( (this.member?.role === 'owner') || (this.member?.role !== 'admin') ) {
        return false;
      }
      return true;
    },

    show_remove_member_button() {

      // don't show if i'm the member--we'll show the 'leave club' button instead
      if ( this.member._id === userStore.getUserId() ) {
        return false;
      }

      // don't show if user isn't an admin
      if ( (this.user_role !== 'owner') && (this.user_role !== 'admin') ) {
        return false;
      }
      // don't show if the member is an owner
      if ( (this.member?.role === 'owner') ) {
        return false;
      }
      return true;
    },

    show_leave_club_button() {
      return (
          ( this.user_role !== 'owner' ) &&
          ( this.member._id === userStore.getUserId() )
      );
    },

    show_transfer_ownership_button() {
      return (
        ( this.user_role === 'owner' ) &&
        ( this.member?.role === 'admin' )
      );
    },
  },
  methods: {

    async askForConfirmation(_message) {
      return new Promise((resolve, reject) => {
        try {
          const confirmed = window.confirm(_message);
          resolve(confirmed);
        }
        catch(err) {
          reject(err);
        }
      });
    },

    async makeAdmin() {
      return this.askForConfirmation(`Make ${this.member.username} an admin of this pool?`)
          .then(confirmed => {
            if ( !confirmed ) {
              return null;
            }
            return axios.post(`/api/club/make-admin`, {
              club_id: this.club_id,
              member_id: this.member._id
            });
          })
          .then(response => {
            if ( response ) {
              this.$emit('club-updated', response.data);
            }
          })
          .catch(err => {
            console.log(err);
          });
    },

    async revokeAdmin() {
      return this.askForConfirmation(`Revoke admin privileges for ${this.member.username}?`)
          .then(confirmed => {
            if ( !confirmed ) {
              return null;
            }
            return axios.post(`/api/club/revoke-admin`, {
              club_id: this.club_id,
              member_id: this.member._id
            });
          })
          .then(response => {
            if ( response ) {
              this.$emit('club-updated', response.data);
            }
          })
          .catch(err => {
            console.log(err);
          });
    },

    async transferClubOwnership() {
      return this.askForConfirmation(`Transfer pool ownership to ${this.member.username}?`)
          .then(confirmed => {
            if ( !confirmed ) {
              return null;
            }
            return axios.post(`/api/club/transfer-ownership`, {
              club_id: this.club_id,
              member_id: this.member._id
            });
          })
          .then(response => {
            if ( response ) {
              this.$emit('club-updated', response.data);
            }
          })
          .catch(err => {
            console.log(err);
          });
    },

    async removeMember() {
      return this.askForConfirmation(`Remove ${this.member.username} from this Oscar Pool?`)
          .then(confirmed => {
            if ( !confirmed ) {
              return null;
            }
            return axios.post(`/api/club/remove`, {
              club_id: this.club_id,
              member_id: this.member._id
            });
          })
          .then(response => {
            if ( response ) {
              this.$emit('club-updated', response.data);
            }
          })
          .catch(err => {
            console.log(err);
          });
    },

    async leaveClub() {
      return this.askForConfirmation(`Are you sure you want to leave this Oscar Pool?`)
          .then(confirmed => {
            if ( !confirmed ) {
              return null;
            }
            return axios.post(`/api/club/leave`, {
              club_id: this.club_id
            });
          })
          .then(response => {
            if ( response ) {
              this.$emit('club-updated', response.data);
            }
          })
          .catch(err => {
            console.log(err);
          });
    },
  }
}
</script>


<style scoped>

.club-member {
  position: relative;
  padding: 0.1em 0.5em 0.5em 0.5em;

  height:2.25em;

  border-style: solid;
  border-width: 0.1px;
  border-radius: 5px;
  border-color: transparent;

  transition: height 0.2s ease-out, border-color 0.5s ease-out;

  a {
    text-decoration: inherit;
    color: inherit;
    cursor: pointer;
    color: var(--bs-link-hover-color);
  }
  a:hover {
    color: var(--bs-link-hover-color);
    font-weight: 600;
  }

  .expand-button {
    display: inline-block;
    float: right;
    margin-top: -5px;
    color: rgba(0, 0, 0, 0.175);

    &:hover {
      color: dimgrey;
    }

  }


  &.expanded {
    height: 3.75em;

    border-style: solid;
    border-width: 0.1px;
    border-radius: 5px;
    border-color: var(--bs-card-border-color);

    .member-name-button-group {

      position: absolute;
      right: 0px;
      bottom: 0px;


      .btn-member-menu {
        font-size: xx-small;
      }

      .btn-leave-club {
        color: red;
      }
      .btn-admin-change {
        color: blue;
      }
      .btn-ownership-change {
        color: var(--mol-color-win-icon);
      }
    }
  }
}
</style>